import React, { useRef, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Trash, Send, Mic, PlayFill, PauseFill } from "react-bootstrap-icons";
import WaveSurfer from "wavesurfer.js";

interface VoiceRecorderUIProps {
  recording: boolean;
  recordTime: number;
  voiceBlob: Blob | null;
  disabled: boolean;
  onStartRecording: () => void;
  onStopRecording: (send?: string) => void;
  onCancelRecording: () => void;
  onResumeRecording: () => void;
  onSendVoice: () => void;
  onDiscardVoice: () => void;
}

export const VoiceRecorderUI: React.FC<VoiceRecorderUIProps> = ({
  recording,
  recordTime,
  voiceBlob,
  disabled,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  onSendVoice,
  onDiscardVoice,
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showRecord, setShowRecord] = useState(true);

  const formatTimeMs = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    const centiseconds = Math.floor((ms % 1000) / 100)
      .toString()
      .padStart(1, "0");
    return `${minutes}:${seconds}.${centiseconds}`;
  };

  useEffect(() => {
    setShowRecord(true);

    if (!voiceBlob) {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
      return;
    }
  
    if (!waveformRef.current) return;
  
    let ws = wavesurferRef.current;
    if (!ws) {
      ws = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#ccc",
        progressColor: "#4a90e2",
        cursorWidth: 0,
        height: 40,
      });
      wavesurferRef.current = ws;
      ws.on("finish", () => setIsPlaying(false));
      ws.on("error", (e) => console.error("WaveSurfer error:", e));
    }
  
    const url = URL.createObjectURL(voiceBlob);
    
    ws.load(url);
  
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [voiceBlob]);

  useEffect(() => {
    if (recording) {
      setShowRecord(true);

      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    }
  }, [recording]);

  const togglePlay = () => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.playPause();
    setIsPlaying(!isPlaying);
  };

  if (!recording && voiceBlob && showRecord) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onDiscardVoice}
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 36, height: 36, padding: 0 }}
          title="Удалить запись"
        >
          <Trash />
        </Button>

        <div ref={waveformRef} style={{ flex: 1, height: 42 }} />
      
        <Button
          variant="outline-secondary"
          onClick={togglePlay}
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 36, height: 36, padding: 0 }}
          title={isPlaying ? "Пауза" : "Воспроизвести"}
        >
          {isPlaying ? <PauseFill /> : <PlayFill />}
        </Button>

        <Button
          variant="outline-secondary"
          onClick={onSendVoice}
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 36, height: 36, padding: 0 }}
          title="Отправить голосовое"
        >
          <Send />
        </Button>
      </div>
    );
  }

  if (recording) {
    return (
      <div style={{ display: "flex", height: 42, alignItems: "center", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "red",
              animation: "blink 1s infinite",
            }}
          />
          <span style={{ minWidth: 50, textAlign: "center" }}>
            {formatTimeMs(recordTime)}
          </span>
        </div>

        <div style={{ flex: 1, textAlign: "center" }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onCancelRecording}
            style={{ minWidth: 80 }}
          >
            Отмена
          </Button>
        </div>

        <Button
          variant="outline-secondary"
          // disabled
          onClick={() => {
            setShowRecord(false)
            onStopRecording("true");
          }}
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 36, height: 36, padding: 0 }}
  title="Остановить и отправить голосовое"
        >
          <Send />
        </Button>

        <style>{`
          @keyframes blink {
            0% { opacity: 1; }
            50% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Button
      variant="outline-secondary"
      onClick={onStartRecording}
      disabled={disabled}
      className="rounded-circle d-flex align-items-center justify-content-center"
      style={{ width: 36, height: 36, padding: 0, marginLeft: "auto" }}
      title="Записать голосовое"
    >
      <Mic />
    </Button>
  );
};
